import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";
import { PublicPageLayout } from "@/components/landing/PublicPageLayout";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="pb-8 border-b border-white/[0.06] last:border-0">
      <h2 className="text-xl font-semibold tracking-[-0.02em] mb-4 text-white">{title}</h2>
      <div className="text-white/50 text-base leading-[1.8]">{children}</div>
    </section>
  );
}

export default function Cookies() {
  return (
    <PublicPageLayout>
      <SEO 
        title="Cookie Policy | Sorvaine"
        description="Learn about how Sorvaine uses cookies and similar technologies to improve your experience on our platform."
        canonical="https://sorvaine.com/cookies"
      />

      <section className="py-20 md:py-28 px-6">
        <motion.div 
          className="max-w-[800px] mx-auto"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-white/50 font-medium">Legal</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-semibold tracking-[-0.04em] leading-tight mb-4">Cookie Policy</h1>
          <p className="text-white/40 text-sm mb-16">Last updated: March 12, 2026</p>

          <div className="space-y-12">
            <LegalSection title="1. What Are Cookies">
              <p>Cookies are small text files that are stored on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.</p>
            </LegalSection>

            <LegalSection title="2. How We Use Cookies">
              <p className="mb-4">We use cookies for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong className="text-white/70">Essential cookies:</strong> Required for the website to function properly</li>
                <li><strong className="text-white/70">Authentication cookies:</strong> To keep you signed in to your account</li>
                <li><strong className="text-white/70">Preference cookies:</strong> To remember your settings and preferences</li>
                <li><strong className="text-white/70">Analytics cookies:</strong> To understand how visitors use our website</li>
                <li><strong className="text-white/70">Security cookies:</strong> To detect and prevent security threats</li>
              </ul>
              <p className="mt-4">When you first visit our website, you will be presented with a cookie consent banner allowing you to accept or decline non-essential cookies.</p>
            </LegalSection>

            <LegalSection title="3. Types of Cookies We Use">
              <div className="space-y-4">
                <div className="p-5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <h3 className="text-base font-medium mb-2 text-white/80">Strictly Necessary Cookies</h3>
                  <p className="text-sm">These cookies are essential for you to use the features of our website. Without these cookies, services like user authentication cannot be provided.</p>
                </div>
                <div className="p-5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <h3 className="text-base font-medium mb-2 text-white/80">Performance Cookies</h3>
                  <p className="text-sm">These cookies collect information about how you use our website, such as which pages you visit most often. This data helps us improve how our website works.</p>
                </div>
                <div className="p-5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <h3 className="text-base font-medium mb-2 text-white/80">Functionality Cookies</h3>
                  <p className="text-sm">These cookies allow our website to remember choices you make and provide enhanced, personalized features.</p>
                </div>
              </div>
            </LegalSection>

            <LegalSection title="4. Third-Party Cookies">
              <p className="mb-4">We currently use the following third-party services that may set cookies:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong className="text-white/70">Supabase:</strong> Authentication and session management cookies required for the Service to function</li>
              </ul>
              <p className="mt-4"><strong className="text-white/70">Plausible Analytics:</strong> We use Plausible for privacy-focused, cookieless site analytics. Plausible does not use cookies, does not collect personal data, and does not track users across sites or devices. No consent is required under GDPR or the ePrivacy Directive for this type of analytics.</p>
              <p className="mt-4">We will update this section as additional services are integrated.</p>
            </LegalSection>

            <LegalSection title="5. Managing Cookies">
              <p className="mb-4">You can control and manage cookies in various ways. Most browsers allow you to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>View what cookies are stored and delete them individually</li>
                <li>Block third-party cookies</li>
                <li>Block cookies from particular websites</li>
                <li>Block all cookies from being set</li>
                <li>Delete all cookies when you close your browser</li>
              </ul>
              <p className="mt-4">Please note that blocking all cookies may impact your experience on our website and limit the functionality available to you.</p>
            </LegalSection>

            <LegalSection title="6. Cookie Duration">
              <p className="mb-4">Cookies can be either:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong className="text-white/70">Session cookies:</strong> Deleted when you close your browser</li>
                <li><strong className="text-white/70">Persistent cookies:</strong> Remain on your device for a set period or until you delete them</li>
              </ul>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/[0.08]">
                      <th className="text-left py-3 pr-4 text-white/70 font-medium">Cookie</th>
                      <th className="text-left py-3 pr-4 text-white/70 font-medium">Type</th>
                      <th className="text-left py-3 pr-4 text-white/70 font-medium">Purpose</th>
                      <th className="text-left py-3 text-white/70 font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/50">
                    <tr className="border-b border-white/[0.04]">
                      <td className="py-3 pr-4 font-mono text-xs text-white/60">sb-access-token</td>
                      <td className="py-3 pr-4">Persistent</td>
                      <td className="py-3 pr-4">User authentication</td>
                      <td className="py-3">Session</td>
                    </tr>
                    <tr className="border-b border-white/[0.04]">
                      <td className="py-3 pr-4 font-mono text-xs text-white/60">sb-refresh-token</td>
                      <td className="py-3 pr-4">Persistent</td>
                      <td className="py-3 pr-4">Session refresh</td>
                      <td className="py-3">1 year</td>
                    </tr>
                    <tr className="border-b border-white/[0.04]">
                      <td className="py-3 pr-4 font-mono text-xs text-white/60">cookie-consent</td>
                      <td className="py-3 pr-4">Persistent</td>
                      <td className="py-3 pr-4">Stores consent preference</td>
                      <td className="py-3">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </LegalSection>

            <LegalSection title="7. Updates to This Policy">
              <p>We may update this Cookie Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. Please revisit this page regularly to stay informed about our use of cookies.</p>
            </LegalSection>

            <LegalSection title="8. Contact Us">
              <p>
                If you have any questions about our use of cookies, please contact us at{" "}
                <a href="mailto:hello@sorvaine.com" className="text-primary hover:underline">hello@sorvaine.com</a>.
              </p>
            </LegalSection>
          </div>
        </motion.div>
      </section>
    </PublicPageLayout>
  );
}
